import { ReactNode, useState } from 'react';
import { cn } from '@/lib/utils.ts';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover.tsx';

const ColorSwatchGrid = ({
  colors,
  currentValue,
  onSelect,
}: {
  colors: { value: string | null; label: string }[];
  currentValue?: string | null;
  onSelect: (value: string | null) => void;
}) => (
  <div className="grid grid-cols-5 gap-1.5">
    {colors.map((color) => (
      <button
        key={color.label}
        type="button"
        title={color.label}
        onClick={() => onSelect(color.value)}
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-md border transition',
          currentValue === color.value
            ? 'border-neutral-900'
            : 'border-neutral-200 hover:border-neutral-400',
        )}
        style={color.value ? { backgroundColor: color.value } : undefined}
      >
        {color.value === null && (
          <span className="text-xs text-neutral-500">×</span>
        )}
      </button>
    ))}
  </div>
);

const CustomColorRow = ({
  currentValue,
  onChange,
}: {
  currentValue?: string;
  onChange: (value: string) => void;
}) => (
  <label className="mt-2 flex cursor-pointer items-center justify-between gap-2 rounded-md border border-neutral-200 px-2 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50">
    <span>Niestandardowy</span>
    <input
      type="color"
      value={currentValue ?? '#000000'}
      onChange={(e) => onChange(e.target.value)}
      className="h-6 w-8 cursor-pointer rounded border border-neutral-200 bg-transparent p-0"
    />
  </label>
);

// Unified picker for any color-style mark (text color, highlight, etc.).
// Caller provides the colors list and the apply callback; this component
// owns the popover state, indicator bar, and swatch grid layout.
export const ColorPickerPopover = ({
  title,
  icon,
  colors,
  currentValue,
  indicatorFallback,
  onSelect,
}: {
  title: string;
  icon: ReactNode;
  colors: { value: string | null; label: string }[];
  currentValue: string | undefined;
  indicatorFallback: string;
  onSelect: (value: string | null) => void;
}) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (value: string | null) => {
    onSelect(value);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={title}
          className="relative flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 transition hover:bg-neutral-50 hover:text-neutral-700"
        >
          {icon}
          <span
            className="absolute bottom-1 left-1 right-1 h-1 rounded-sm"
            style={{ backgroundColor: currentValue ?? indicatorFallback }}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <ColorSwatchGrid
          colors={colors}
          currentValue={currentValue ?? null}
          onSelect={handleSelect}
        />
        <CustomColorRow currentValue={currentValue} onChange={handleSelect} />
      </PopoverContent>
    </Popover>
  );
};
